import { action, computed, observable, reaction, comparer } from 'mobx';
import type { IRootStore } from './IRootStore';

interface PendoUserSubscription {
  subscription: string | null;
  region?: string;
  subscription_start_time?: string;
  subscription_end_time?: string;
}

interface PendoEntitlements {
  entitlements: string[];
}

interface PendoVisitor extends PendoUserSubscription, PendoEntitlements {
  id: string;
  sessionid?: string;
  country?: string;
  platform?: string;
  rhlsCourseSku?: string[];
  hasPersonalData: boolean;
}

interface PendoAccount {
  id: string;
  globalCustomerName?: string;
}

interface PendoPayload {
  visitor: PendoVisitor;
  account?: PendoAccount;
}

class PendoStore {
  rootStore: IRootStore;

  // THIS CAN BE PART OF OLE-CONFIG
  COURSES_TO_TRACK = [
    'rh124-8.2',
    'rh124vc-8.0',
    'rh134-8.2',
    'rh134vc-8.0',
    'do180-4.6',
    'do180vc-4.6',
    'rh294-8.0',
    'rh294-8.4',
    'do280-4.6',
    'do288-4.6',
    'do250-1.0',
    'do101-4.6',
  ];

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    reaction(
      () => this.pendoPayload,
      (payload) => {
        if (!payload) return;
        this.applyPendoPayload(payload);
      },
      { fireImmediately: true, equals: comparer.structural },
    );
  }

  @observable isPendoInitialized = false;

  @observable pendoProgressWatcher: unknown = null;

  @observable currentUserSubscription: PendoUserSubscription | null = null;

  @computed get usersToTrack(): string[] | undefined {
    return this.rootStore?.configStore?.pendoUsersToTrack;
  }

  @computed get userSubscription(): PendoUserSubscription {
    const { subscription } = this.rootStore.userStore;
    if (this.rootStore.userStore.isLoggedIn && subscription?.subscription) {
      const subscriptionData: PendoUserSubscription = {
        ...(subscription.subscription && {
          subscription: subscription.subscription,
        }),
        ...(subscription.region && {
          region: subscription.region,
        }),
        ...(subscription.start_time && {
          subscription_start_time: subscription.start_time,
        }),
        ...(subscription.end_time && {
          subscription_end_time: subscription.end_time,
        }),
      };
      return subscriptionData;
    }
    return { subscription: null };
  }

  @computed get entitlements(): PendoEntitlements {
    const values: string[] = [];

    if (this.rootStore.userStore.isOpenSubscriber) {
      values.push('open');
    }

    if (this.rootStore.userStore.hasVT) {
      values.push('vt');
    }

    if (this.rootStore.userStore.hasAlaCarteEnrollments) {
      values.push('alacarte');
    }

    if (this.rootStore.userStore.hasRhlsCourseEnrollment) {
      values.push('rhls_course');
    }

    return { entitlements: values };
  }

  @computed get visitor(): PendoVisitor | null {
    const { user, name } = this.rootStore.userStore;
    let visitorData: PendoVisitor | null = null;
    if (
      this.rootStore.userStore.isLoggedIn &&
      user?.uuid &&
      this.isUserAllowedToBeTracked(user?.username)
    ) {
      visitorData = {
        id: user.uuid,
        ...(user.sessionid && { sessionid: user.sessionid }),
        ...(user.default_country && { country: user.default_country }),
        ...(this.rootStore?.configStore?.rhtDeployment && {
          platform: this.rootStore.configStore.rhtDeployment,
        }),
        ...this.userSubscription,
        ...this.entitlements,
        rhlsCourseSku: this.rootStore.userStore.currentRhlsCourseSkus,
        hasPersonalData: (name?.split(/\s+/).length ?? 0) >= 2,
      };
    }

    return visitorData;
  }

  @computed get account(): PendoAccount | null {
    const { subscription } = this.rootStore.userStore;

    const accountName = subscription?.account_name;

    if (
      this.rootStore.userStore.isLoggedIn &&
      subscription?.subscription &&
      accountName
    ) {
      const accountData: PendoAccount = {
        id: accountName,
        ...(subscription.global_customer_name && {
          globalCustomerName: subscription.global_customer_name,
        }),
      };

      return accountData;
    }
    return null;
  }

  @computed get pendoPayload(): PendoPayload | null {
    const { visitor, account } = {
      visitor: this.visitor,
      account: this.account,
    };
    if (!visitor) return null;

    return {
      visitor,
      ...(account ? { account } : {}),
    };
  }

  @action applyPendoPayload(payload: PendoPayload): void {
    // Pendo snippet may not be loaded yet
    if (!window?.pendo) return;

    if (!this.isPendoInitialized) {
      try {
        window.pendo.initialize(payload);
        this.isPendoInitialized = true;
        return;
      } catch (e) {
        this.isPendoInitialized = false;
        console.error('Pendo initialize failed', e);
        return;
      }
    }

    try {
      window.pendo.updateOptions(payload);
    } catch (e) {
      console.error('Pendo updateOptions failed', e);
    }
  }

  @action initPendo(): void {
    // Backwards-compatible: if something still calls initPendo(),
    // it will initialize/update using the same payload logic.
    const payload = this.pendoPayload;
    if (!payload) return;
    this.applyPendoPayload(payload);
  }

  @computed get courseProgress(): Record<string, unknown> | null {
    const { progressStore } = this.rootStore;

    const progressToSend = this.COURSES_TO_TRACK?.map((courseSlug) => {
      const courseProgressItem = progressStore.progressDict[courseSlug];
      return courseProgressItem && courseProgressItem.total_progress
        ? {
            [courseSlug?.replace('-', '_')?.replace('.', '_')]:
              courseProgressItem.total_progress,
          }
        : null;
    })?.filter((p): p is Record<string, number> => p !== null);

    return progressToSend && progressToSend.length > 0
      ? Object.assign(
          {},
          ...progressToSend,
          progressStore.lastAccessProgress &&
            (typeof progressStore.lastAccessProgress['@timestamp'] === 'string'
              ? {
                  last_access_date:
                    progressStore.lastAccessProgress['@timestamp'],
                }
              : {}),
        )
      : null;
  }

  @action sendSubscription(): void {
    // Backwards-compatible: if something still calls sendSubscription(),
    // it will initialize/update using the same payload logic.
    const payload = this.pendoPayload;
    if (!payload) return;
    this.applyPendoPayload(payload);
  }

  @action isUserAllowedToBeTracked(username: string | undefined): boolean {
    const isTrackAllUsersEnabled =
      this.rootStore?.configStore?.pendoTrackALLUsers || false;

    if (isTrackAllUsersEnabled) return true;

    // guard against early boot when username is not ready yet
    if (!username || typeof username !== 'string') return false;

    const usersToTrack = this.rootStore?.configStore?.pendoUsersToTrack ?? [];
    return usersToTrack.some((u) => {
      const candidate = typeof u === 'string' ? u.trim().toLowerCase() : '';
      return candidate === username.trim().toLowerCase();
    });
  }

  /**
   * https://support.pendo.io/hc/en-us/articles/360032294291-Configure-Track-Events
   */
  @action sendTrackEvent(
    eventName: string,
    params?: Record<string, unknown>,
  ): void {
    const payload = {
      ...params,
      event_timestamp_ms: Date.now(),
    };
    if (
      this.isPendoInitialized &&
      this.isUserAllowedToBeTracked(this.rootStore.userStore.username)
    ) {
      // window.pendo.enableDebugging();
      window.pendo?.track?.(eventName, payload);
    }
  }

  @action showGuideById(guideId: string): boolean {
    // https://agent.pendo.io/public/Guides/
    if (
      guideId &&
      this.isPendoInitialized &&
      this.isUserAllowedToBeTracked(this.rootStore.userStore.username)
    ) {
      return window.pendo?.showGuideById?.(guideId) ?? false;
    }
    return false;
  }

  @action showGuideByName(guideName: string): boolean {
    if (
      guideName &&
      this.isPendoInitialized &&
      this.isUserAllowedToBeTracked(this.rootStore.userStore.username)
    ) {
      return Boolean(window.pendo?.showGuideByName?.(guideName));
    }
    return false;
  }
}

export default PendoStore;
